import React, {useContext, useEffect, useMemo, useState} from "react";
import {AppContextContext, InterestServiceContext} from "../../Contexts";
import {Breadcrumb, Button, message, Modal, Table} from "antd";
import {DeleteFilled, PlusOutlined, ReloadOutlined} from "@ant-design/icons";
import {Link, useLocation, useNavigate} from "react-router-dom";
import _ from "lodash";
import * as globalStyles from "../App.module.css";
import Interest from "../../domain/Interest";
import {useTableHandler} from "../../sal-ui/TableHandler";
import PagedResult from "../../service/PagedResult";
import {DocumentTitle} from "../DocumentTitle";
import {ColumnsType} from "antd/es/table";
import {Permission} from "../../domain/auth/Permission";
import {SingleSummaryBlock} from "../common/SummaryBlock";
import * as styles from "./InterestList.module.css";
import {INTEREST_DELETE_WARNING} from "./InterestDeleteWarning";
import {renderOrganizationLink} from "../common/OrganizationLink";
import Tip from "../common/Tip";
import InterestFilter, {createDefaultInterestFilterValues} from "./InterestFilter";
import {FormPersister} from "../../sal-ui/FormPersister";
import {ascendingFirstSortDirections} from "../../sal-ui/TableSortDirections";

const PERSISTENT_IDENT = "InterestList";
const FILTER_DEBOUNCE_MS = 500;

function InterestList() {
    const appContext = useContext(AppContextContext);
    const interestService = useContext(InterestServiceContext);
    const location = useLocation();
    const navigate = useNavigate();
    const [interests, setInterests] = useState<PagedResult<Interest>>();
    const [filterValues, setFilterValues] = useState<any>();
    const filterFormPersister = useMemo(() => new FormPersister(PERSISTENT_IDENT), []);
    const debouncedOnFinishFilter = useMemo(() => _.debounce(onFinishFilter, FILTER_DEBOUNCE_MS), []);

    const title = "Interests";
    const hasManage = appContext.user.isSystemAdmin() || appContext.user.hasPermission(Permission.INTEREST__MANAGE_ORG);

    const tableHandler = useTableHandler("name", {
        reloadFunction: reload,
        persistentIdent: PERSISTENT_IDENT
    });

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        if (!appContext.user.isSystemAdmin()) {
            tableHandler.reload();

            return;
        }

        const searchParams = new URLSearchParams(location.search);
        const loadedValues = searchParams.has("filter")
            ? filterFormPersister.loadValues(FormPersister.decodeUrlState(searchParams.get("filter")!))
            : filterFormPersister.loadValues();

        const defaultValues = createDefaultInterestFilterValues();
        const values = _.isEmpty(loadedValues)
            ? defaultValues
            : {...defaultValues, ...loadedValues};

        setFilterValues(values);

        if (searchParams.has("filter")) {
            navigate(location.pathname, {replace: true});
        }
    }, []);

    useEffect(() => {
        if (appContext.user.isSystemAdmin() && filterValues !== undefined) {
            debouncedOnFinishFilter(filterValues);
        }
    }, [filterValues]);

    return (
        <DocumentTitle title={title}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]}
                            items={[
                                {title: appContext.config?.appName},
                                {title}
                            ]}
                />

                <h1>{title}</h1>

                <Tip
                    id="InterestList.purpose"
                    style={{marginBottom: 12}}
                    message="Interests define topics, technologies, products, or threat areas that matter to the organization."
                    description={
                        <>
                            SEAL uses them to derive{" "}
                            <Link to="/definitions/ai-attributes">AI attributes</Link>,{" "}
                            <Link to="/definitions/ai-extractors">AI extractors</Link>, and{" "}
                            <Link to="/vul-source-items/views">views</Link>{" "}
                            for relevance evaluation. Each organization can have at most 10 interests.
                        </>
                    }
                />

                {appContext.user.isSystemAdmin() &&
                    <InterestFilter
                        className={styles.filter}
                        values={filterValues}
                        persistentStateIdent={PERSISTENT_IDENT}
                        onChange={setFilterValues}
                    />
                }

                <div className={globalStyles["common__top-button-bar"]}>
                    {hasManage &&
                        <Button type={"primary"}
                                icon={<PlusOutlined/>}
                                onClick={() => navigate("/interests/add")}>
                            Add
                        </Button>
                    }
                    <Button className={"btn-seamless"} icon={<ReloadOutlined/>} onClick={tableHandler.reload}/>
                </div>

                <Table
                    className={styles.table}
                    dataSource={interests?.data}
                    loading={tableHandler.loading}
                    pagination={tableHandler.pagination}
                    showSorterTooltip={false}
                    rowKey="id"
                    onChange={tableHandler.onTableChange}
                    tableLayout={"fixed"}
                    columns={getColumns()}
                />
            </>
        </DocumentTitle>
    );

    function getColumns(): ColumnsType<Interest> {
        const cols: ColumnsType<Interest> = [
            {
                dataIndex: "name",
                title: "Name",
                width: appContext.user.isSystemAdmin() ? 430 : 560,
                sorter: true,
                sortDirections: ascendingFirstSortDirections,
                defaultSortOrder: "ascend",
                render: renderName
            }
        ];

        if (appContext.user.isSystemAdmin()) {
            cols.push({
                dataIndex: ["organization", "name"],
                key: "organization.name",
                title: "Organization",
                width: 260,
                sorter: true,
                sortDirections: ascendingFirstSortDirections,
                render: renderOrganization
            });
        }

        cols.push({
            title: "Scope restriction",
            width: 260,
            render: renderScopeRestriction
        });

        if (hasManage) {
            cols.push({
                title: "Actions",
                width: 100,
                className: globalStyles["table-actions"],
                render: renderAction
            });
        }

        return cols;
    }

    function renderName(value: unknown, interest: Interest) {
        return <>
            <div><Link to={`/interests/${interest.id}`}>{value}</Link></div>
            {interest.description &&
                <SingleSummaryBlock>
                    <span style={{whiteSpace: "pre-wrap"}}>{interest.description}</span>
                </SingleSummaryBlock>
            }
        </>;
    }

    function renderOrganization(value: string | undefined, interest: Interest) {
        return renderOrganizationLink(interest.organization, value);
    }

    function renderScopeRestriction(_value: unknown, interest: Interest) {
        const type = interest.restrictionType ?? "NONE";

        if (type === "NONE") {
            return <i>without restriction</i>;
        }

        if (type === "VIEW") {
            if (!interest.restrictedView) {
                return <i>no view</i>;
            }

            return (
                <>
                    <div style={{marginBottom: 8}}><i>match selected view:</i></div>
                    {interest.restrictedView.id
                        ? <Link to={`/vul-source-items/views/${interest.restrictedView.id}`}>
                            {interest.restrictedView.name || interest.restrictedView.id}
                        </Link>
                        : interest.restrictedView.name}
                </>
            );
        }

        if (!interest.restrictedSources || interest.restrictedSources.length === 0) {
            return <i>no sources</i>;
        }

        return (
            <>
                <div style={{marginBottom: 8}}><i>{type === "ALLOW_SOURCES" ? "allow selected sources:" : "block selected sources:"}</i></div>
                {interest.restrictedSources.map(source => (
                    <div key={source.id ?? source.name}>
                        {source.id
                            ? <Link to={`/vul-sources/${source.id}`}>{source.name || source.id}</Link>
                            : source.name}
                    </div>
                ))}
            </>
        );
    }

    function renderAction(_value: unknown, interest: Interest) {
        return (
            <Button
                icon={<DeleteFilled/>}
                className={"ant-btn-icon-only"}
                title={"Delete"}
                onClick={() => {
                    Modal.confirm({
                        content: <>Do you really want to delete interest <b>{interest.name}</b>? {INTEREST_DELETE_WARNING}</>,
                        okText: "Delete",
                        cancelText: "Cancel",
                        onOk: () => onDeleteConfirm(interest)
                    });
                }}
            />
        );
    }

    function onDeleteConfirm(interest: Interest) {
        interestService.delete(interest)
            .then(() => {
                message.success("Interest deleted.");

                tableHandler.reload();
            });
    }

    function onFinishFilter(values: any) {
        const rqbQuery = createRqbQuery(values);

        filterFormPersister.saveState(values);

        tableHandler.onRqbSearchSubmit(rqbQuery);
    }

    function createRqbQuery(values: any) {
        const rqbQuery = {
            combinator: "and",
            rules: []
        };

        if (values.organizationIds?.length > 0) {
            rqbQuery.rules.push({
                field: "organization.id",
                operator: "in",
                value: values.organizationIds
            });
        }

        return rqbQuery;
    }

    function reload() {
        return interestService.getList(tableHandler.queryOptions)
            .then(result => {
                tableHandler.updateTotal(result.total);

                setInterests(result);
            });
    }
}

export default InterestList;
